/**
 * The peer pane while the other party is on the way: who is coming, what the
 * call is really doing, and the tenant's waiting splash that fills the pane
 * until their first video frame.
 *
 * Shared by the web layout (components/web/PeerWaitingPane.web.tsx) and the
 * native one (components/PeerWaitingPane.native.tsx). Everything here is pure
 * so both platforms and their tests read the same rules.
 *
 * The one rule that matters: the stage line never claims a step that has not
 * happened. Each stage is derived from a real Redux event (callStage.ts for
 * the connection and conference, the participants and tracks state for the
 * peer), and a stall past SLOW_STAGE_MS is said out loud instead of being
 * left to look like progress.
 */
import { isVideoMailRoom } from '../voicemail/recordingRoom';
// Names that label nobody, so the pane must not greet them as a person:
//   - 'Direct call' / 'Group call': persistPlatformCall (client-profile.js)
//     and enterPlatformCallRoom (interpreter-queue/middleware.ts) store them
//     when they know no name;
//   - 'Unknown': what vrs-server's p2p_join_room sent for a caller before it
//     sent null, and what an older server still sends;
//   - 'Malka contact': the server's ring and push fallback
//     (vrs-server/ws/p2p-handlers.js notifyPlatformIncomingCall).
const PLACEHOLDER_NAMES = new Set(['direct call', 'group call', 'unknown', 'malka contact']);
const MAX_NAME_LENGTH = 80;
function isRecord(value) {
    return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}
/**
 * Room names compare decoded and case-insensitively: the conference state may
 * hold the URL-encoded form, and the stored call holds the server's form.
 */
export function normalizeRoomName(value) {
    if (typeof value !== 'string') {
        return '';
    }
    let room = value.trim();
    try {
        room = decodeURIComponent(room);
    }
    catch {
        // A malformed escape is compared as typed.
    }
    return room.toLowerCase();
}
/**
 * A usable peer name, tidied and capped, or '' for a missing value or one of
 * the placeholders above.
 */
export function cleanPeerName(value) {
    if (typeof value !== 'string') {
        return '';
    }
    const name = value.replace(/\s+/g, ' ').trim();
    if (!name || PLACEHOLDER_NAMES.has(name.toLowerCase())) {
        return '';
    }
    return name.length > MAX_NAME_LENGTH ? `${name.slice(0, MAX_NAME_LENGTH - 1)}…` : name;
}
/**
 * The kind of party the local person is waiting for.
 *
 * An interpreter always waits for the client. A client waits for the other
 * person in a direct (`p2p-`) call, for everyone in a `group-` call, and for
 * an interpreter everywhere else (queue `vrs-`/`vri-` rooms, VRI sessions).
 */
export function getPeerKind(localRole, roomName) {
    if (localRole === 'interpreter') {
        return 'client';
    }
    const room = normalizeRoomName(roomName);
    if (room.startsWith('p2p-')) {
        return 'peer';
    }
    if (room.startsWith('group-')) {
        return 'group';
    }
    return 'interpreter';
}
/**
 * The peer's name, read only from a stored call that belongs to this room.
 *
 * `vrs_active_call` lives in localStorage and outlives its call, so a name is
 * used only when the stored room is the current room (room names are random
 * per call) and, when the record says whose it is, the stored role is ours.
 *
 * Sources, per the writers of `vrs_active_call`:
 *   - interpreter: `clientName` (interpreter-profile.html, Terp's persistMatchData)
 *   - client in a queue call: `interpreterName` (client-profile.js from the
 *     match message; native persistMatchData)
 *   - client in a direct call: `clientName`, which the platform-call writers
 *     use for the other person's name
 *   - group call: none; there is no single peer to name
 */
export function resolveStoredPeerName(kind, roomName, storedCall, localRole) {
    if (!isRecord(storedCall)) {
        return '';
    }
    const room = normalizeRoomName(roomName);
    if (!room || normalizeRoomName(storedCall.roomName) !== room) {
        return '';
    }
    const storedRole = storedCall.role;
    if (localRole && (storedRole === 'client' || storedRole === 'interpreter') && storedRole !== localRole) {
        return '';
    }
    switch (kind) {
        case 'interpreter':
            return cleanPeerName(storedCall.interpreterName);
        case 'client':
        case 'peer':
            return cleanPeerName(storedCall.clientName);
        default:
            return '';
    }
}
/**
 * Whether this room is a VideoMail recording: its name says so
 * (voicemail/recordingRoom.ts), or the stored call for this very room was
 * rewritten for a VideoMail relay (conference/components/*Conference.tsx,
 * VideoMailGreetingRecordingOverlay.tsx). Nobody is on the way there: the
 * person records alone with a hidden recorder.
 */
function isVideoMailRecording(roomName, storedCall) {
    if (isVideoMailRoom(normalizeRoomName(roomName))) {
        return true;
    }
    return isRecord(storedCall)
        && storedCall.videoMailRelay === true
        && Boolean(normalizeRoomName(roomName))
        && normalizeRoomName(storedCall.roomName) === normalizeRoomName(roomName);
}
/**
 * What the peer pane waits for, or undefined when this viewer gets no peer
 * pane (guests, observers and hearing parties keep today's layout, and a
 * VideoMail recording has no partner to wait for).
 */
export function resolvePeerWaiting({ localRole, roomName, storedCall }) {
    if (localRole !== 'client' && localRole !== 'interpreter') {
        return undefined;
    }
    if (isVideoMailRecording(roomName, storedCall)) {
        return undefined;
    }
    const kind = getPeerKind(localRole, roomName);
    return {
        kind,
        name: resolveStoredPeerName(kind, roomName, storedCall, localRole),
        paneRole: localRole === 'interpreter' ? 'client' : 'interpreter'
    };
}
/** How long a step may run before the line says so plainly. */
export const SLOW_STAGE_MS = 20000;
const SLOWABLE_STAGES = new Set([
    'opening', 'connecting', 'waiting', 'peer_joined', 'video_starting'
]);
/** Whether a stage has a "taking longer than it should" wording. */
export function isSlowableStage(stage) {
    return SLOWABLE_STAGES.has(stage);
}
const JOINED_STAGES = new Set([
    'waiting', 'peer_joined', 'video_starting', 'camera_off', 'live'
]);
/** Whether the conference is joined at this stage (the splash loads its videos only then). */
export function isJoinedStage(stage) {
    return JOINED_STAGES.has(stage);
}
/**
 * The peer's video fact from what each platform can observe.
 *
 * "starting" is claimed only once a camera track exists or the peer has
 * signalled an unmuted camera source: a present participant with neither may
 * have joined with the camera off (Jitsi creates no track for a camera that
 * starts muted), so that reads as 'none', never as video on its way.
 */
export function resolvePeerVideo({ cameraSources, rendered, track }) {
    if (rendered) {
        return 'started';
    }
    if (track) {
        return track.muted ? 'muted' : 'starting';
    }
    const cameras = Array.from(cameraSources || [])
        .filter(source => source && (source.videoType === undefined || source.videoType === 'camera'));
    if (cameras.some(source => source.muted === false)) {
        return 'starting';
    }
    return cameras.some(source => source.muted === true) ? 'muted' : 'none';
}
/**
 * The current stage from the call-room slice and the peer's pane facts.
 *
 * An end or a failure wins: a failed connection is not "Connecting…". A
 * failure the app is retrying reads as reconnecting. The peer's own presence
 * and first frame come next, because they are the strongest evidence there
 * is. Then the conference and the connection, in that order.
 */
export function deriveCallStage(room, peer) {
    if (room?.conference === 'ended') {
        return 'ended';
    }
    if (room?.connection === 'failed') {
        return 'connection_failed';
    }
    if (room?.conference === 'failed') {
        return 'conference_failed';
    }
    if (room?.connection === 'retrying' || room?.conference === 'retrying') {
        return 'reconnecting';
    }
    if (room?.conference === 'left') {
        return 'left';
    }
    if (peer.present) {
        switch (peer.video) {
            case 'started':
                return 'live';
            case 'muted':
                return 'camera_off';
            case 'starting':
                return 'video_starting';
            default:
                return 'peer_joined';
        }
    }
    if (room?.conference === 'joined') {
        return 'waiting';
    }
    return room?.connection === 'established' ? 'connecting' : 'opening';
}
/**
 * The stage line for a stage, or undefined when there is nothing true to say
 * (before the call server answers, once the video is live, after leaving).
 *
 * "starting video" is said only at video_starting, once a camera track or an
 * unmuted camera source exists; a peer with no video signalled has "joined".
 */
export function describeCallStage(stage, { kind, name, slow }) {
    const text = (key, nameAtStart = false, tone = 'info') => ({ key, kind, name, nameAtStart, tone });
    switch (stage) {
        case 'opening':
            return slow ? text('stillConnecting') : undefined;
        case 'connecting':
            return text(slow ? 'stillConnecting' : 'connecting');
        case 'waiting':
            if (kind === 'group') {
                return text(slow ? 'stillWaitingForGroup' : 'waitingForGroup');
            }
            return text(slow ? 'stillWaitingFor' : 'waitingFor');
        case 'peer_joined':
            return text(slow ? 'videoSlow' : 'peerJoined', true);
        case 'video_starting':
            return text(slow ? 'videoSlow' : 'videoStarting', true);
        case 'camera_off':
            return text('peerCameraOff', true);
        case 'reconnecting':
            return text('reconnecting');
        case 'connection_failed':
            return text('connectionFailed', false, 'problem');
        case 'conference_failed':
            return text('conferenceFailed', false, 'problem');
        case 'ended':
            return text('ended');
        default:
            return undefined;
    }
}
/** The memory after this render; the same object when nothing changed. */
export function trackPeerNameMemory(memory, present, storedName) {
    if (present) {
        return memory.present && memory.occupiedWith === storedName
            ? memory
            : { ...memory, occupiedWith: storedName, present: true };
    }
    return memory.present ? { departed: memory.occupiedWith, present: false } : memory;
}
/**
 * The name the pane shows. Once someone is in the pane it is theirs alone, or
 * the role noun ('' here): a nameless participant never inherits the stored
 * name, because they may not be the person it named. Before anyone arrives it
 * is the stored name, unless that person has already been and left (a later
 * rewrite of the stored call with a new name is used again).
 */
export function resolvePeerDisplayName({ memory, present, presentName, storedName }) {
    if (present) {
        return cleanPeerName(presentName);
    }
    return storedName && storedName !== memory.departed ? storedName : '';
}
const I18N_ROOT = 'vrs.layout.peerWaiting';
/**
 * The noun that names the peer when no name is known: "your interpreter" in
 * a sentence, "Your interpreter" when it opens one or stands as the title.
 */
export function formatPeerNoun(kind, t, atStart) {
    return String(t(`${I18N_ROOT}.${atStart ? 'nounStart' : 'noun'}.${kind}`));
}
/**
 * The pane's name line: the peer's name, or the capitalised noun. A group
 * call has no one peer, so it is titled as the call.
 */
export function formatPeerTitle(peer, t) {
    if (peer.name) {
        return peer.name;
    }
    return peer.kind === 'group' ? String(t(`${I18N_ROOT}.groupTitle`)) : formatPeerNoun(peer.kind, t, true);
}
/** The translated stage line. */
export function formatCallStage(text, t) {
    const name = text.name || formatPeerNoun(text.kind, t, text.nameAtStart);
    return String(t(`${I18N_ROOT}.${text.key}`, { name }));
}
const MEDIA_KEYS = ['video', 'poster', 'signedWelcomeVideo'];
const ASSET_PATH_RE = /^[A-Za-z0-9][A-Za-z0-9._-]*(\/[A-Za-z0-9][A-Za-z0-9._-]*)*$/;
const HOST_RE = /^[A-Za-z0-9.-]+(:\d+)?$/;
function nativeMediaOrigin(config) {
    const domains = isRecord(config.domains) ? config.domains : undefined;
    const api = typeof domains?.api === 'string' ? domains.api.trim() : '';
    return api && HOST_RE.test(api) ? `https://${api}` : undefined;
}
/**
 * A configured media reference as a URL this platform can load, or undefined.
 *
 * Accepted: https URLs, same-origin paths (`/media/x.mp4`) and bare asset
 * names under images/ (`malka-waiting.mp4`), the same convention the tenant
 * `assets` use. Everything else (http, data:, javascript:, protocol-relative,
 * `..`) is rejected rather than loaded. Native has no page origin, so paths
 * resolve against the tenant's API domain.
 */
export function resolveWaitingMediaUrl(value, platform, origin) {
    if (typeof value !== 'string') {
        return undefined;
    }
    const trimmed = value.trim();
    if (!trimmed || trimmed.length > 2048 || /\s/.test(trimmed) || trimmed.includes('..')) {
        return undefined;
    }
    if (/^https:\/\/[A-Za-z0-9.-]+(:\d+)?(\/|$)/i.test(trimmed)) {
        return trimmed;
    }
    let path;
    if (trimmed.startsWith('/') && !trimmed.startsWith('//') && ASSET_PATH_RE.test(trimmed.slice(1))) {
        path = trimmed;
    }
    else if (ASSET_PATH_RE.test(trimmed)) {
        path = `images/${trimmed}`;
    }
    if (!path) {
        return undefined;
    }
    if (platform === 'web') {
        return path;
    }
    return origin ? `${origin}${path.startsWith('/') ? '' : '/'}${path}` : undefined;
}
/**
 * The tenant's splash media, with every unusable entry dropped. An unknown
 * tenant, a missing block or all-empty fields resolve to `{}`, which means
 * the brand animation.
 */
export function resolveRoomWaitingMedia(config, platform) {
    if (!isRecord(config) || !isRecord(config.operations) || !isRecord(config.operations.roomWaitingMedia)) {
        return {};
    }
    const raw = config.operations.roomWaitingMedia;
    const origin = platform === 'native' ? nativeMediaOrigin(config) : undefined;
    const media = {};
    for (const key of MEDIA_KEYS) {
        const url = resolveWaitingMediaUrl(raw[key], platform, origin);
        if (url) {
            media[key] = url;
        }
    }
    return media;
}
const HEX_RE = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;
function hexColor(value) {
    if (typeof value !== 'string' || !HEX_RE.test(value.trim())) {
        return undefined;
    }
    const hex = value.trim();
    return hex.length === 4
        ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`.toUpperCase()
        : hex.toUpperCase();
}
function channels(hex) {
    return [1, 3, 5].map(index => parseInt(hex.slice(index, index + 2), 16));
}
/** WCAG relative luminance of a #RRGGBB colour. */
export function relativeLuminance(hex) {
    const [r, g, b] = channels(hex).map(channel => {
        const value = channel / 255;
        return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
    });
    return (0.2126 * r) + (0.7152 * g) + (0.0722 * b);
}
/** A #RRGGBB colour at an alpha, as rgba() for CSS and React Native alike. */
export function withAlpha(hex, alpha) {
    const [r, g, b] = channels(hexColor(hex) || '#000000');
    return `rgba(${r}, ${g}, ${b}, ${Math.min(1, Math.max(0, alpha))})`;
}
/**
 * Splash colours from the tenant theme. Maple's accent is black, which would
 * draw invisible rings on its black ground, so the ring takes the first of
 * accent, primaryLight and textPrimary that is light enough to see.
 */
export function resolveWaitingSplashPalette(theme) {
    const colours = isRecord(theme) ? theme : {};
    const base = hexColor(colours.surfaceDark) || '#040404';
    const glow = hexColor(colours.primary) || '#0D1A38';
    const ring = [colours.accent, colours.primaryLight, colours.textPrimary]
        .map(hexColor)
        .find((colour) => Boolean(colour) && relativeLuminance(colour) >= 0.06)
        || '#FFFFFF';
    return { base, glow, ring };
}
